import { v4 as uuidv4 } from "uuid";

enum RecombeeStorageKey {
  Sessions = "recombee_sessions",
  Recomms = "recombee_recomms",
}

const MINUTES_PER_TIMEOUT = 30;
const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;
const SESSION_TIMEOUT_MS = MINUTES_PER_TIMEOUT * SECONDS_PER_MINUTE * MS_PER_SECOND;

interface CharacterSession {
  sessionId: string;
  conversationId: string;
  startTime: number;
  lastMessageTime: number;
  messageCount: number;
  recommId?: string;
}

type SessionsMap = Record<string, CharacterSession>;
type RecommsMap = Record<string, string>;

function getSessionsMap(): SessionsMap {
  try {
    return JSON.parse(sessionStorage.getItem(RecombeeStorageKey.Sessions) ?? "{}") as SessionsMap;
  } catch {
    return {};
  }
}

function setSessionsMap(map: SessionsMap): void {
  sessionStorage.setItem(RecombeeStorageKey.Sessions, JSON.stringify(map));
}

function getRecommsMap(): RecommsMap {
  try {
    return JSON.parse(sessionStorage.getItem(RecombeeStorageKey.Recomms) ?? "{}") as RecommsMap;
  } catch {
    return {};
  }
}

function setRecommsMap(map: RecommsMap): void {
  sessionStorage.setItem(RecombeeStorageKey.Recomms, JSON.stringify(map));
}

function createNewSession(characterId: string, conversationId: string, initialMessageCount = 0): CharacterSession {
  const sessionsMap = getSessionsMap();
  const now = Date.now();

  // Claim any pending recommId for this character
  const recommsMap = getRecommsMap();
  const pendingRecommId = recommsMap[characterId];

  const newSession: CharacterSession = {
    sessionId: uuidv4(),
    conversationId,
    startTime: now,
    lastMessageTime: now,
    messageCount: initialMessageCount,
    ...(pendingRecommId && { recommId: pendingRecommId }),
  };

  // Remove from pending if claimed
  if (pendingRecommId) {
    delete recommsMap[characterId];
    setRecommsMap(recommsMap);
  }

  sessionsMap[characterId] = newSession;
  setSessionsMap(sessionsMap);
  return newSession;
}

export function getOrCreateSession(characterId: string, conversationId: string): CharacterSession {
  const map = getSessionsMap();
  const existing = map[characterId];

  if (existing && existing.conversationId === conversationId) {
    return existing;
  }

  return createNewSession(characterId, conversationId);
}

export function updateSessionMessageCount(characterId: string, conversationId: string): CharacterSession {
  const map = getSessionsMap();
  const session = map[characterId];
  const now = Date.now();

  // Create new session if:
  // 1. No existing session
  // 2. Different conversation
  // 3. Last message was > 30 minutes ago (or lastMessageTime missing for backward compat)
  const isTimeout = session && (!session.lastMessageTime || now - session.lastMessageTime > SESSION_TIMEOUT_MS);

  if (!session || session.conversationId !== conversationId || isTimeout) {
    return createNewSession(characterId, conversationId, 1);
  }

  const updated: CharacterSession = {
    ...session,
    messageCount: session.messageCount + 1,
    lastMessageTime: now,
  };

  map[characterId] = updated;
  setSessionsMap(map);
  return updated;
}

export function storeRecommId(characterId: string, recommId: string): void {
  const map = getRecommsMap();
  map[characterId] = recommId;
  setRecommsMap(map);
}
